import { useEffect, useRef, useState, type CSSProperties, type DragEvent } from "react";
import { cn } from "@/lib/cn.js";
import { colorForKey, colorForKeyBorder, formatTime } from "@/lib/format.js";
import type { Appointment, Customer, Service, SyncConflict } from "@/lib/types.js";
import { BannerStack } from "@/views/banner-stack.js";
import { ROW_HEIGHT_PX, MINUTES_PER_ROW, snapMinutes } from "./constants.js";

/**
 * PRD calendar-ux-parity REQ-CUX-006/007/008. A unified appointment block used
 * by Day, Week, and Resource views. The bottom 6px is a resize handle: on
 * mousedown the block tracks the cursor delta, snapping to the appointment's
 * service.slot_increment_minutes (REQ-CUX-007). On mouseup the new ends_at
 * is committed via the parent's onResize callback (which calls
 * PATCH /api/v1/appointments/:id; see REQ-CUX-006).
 *
 * The block is also `draggable` for the existing day-view reschedule path
 * (REQ-CUX-002 regression). When the user grabs the resize handle, the
 * outer drag is disabled by stopping propagation so the resize gesture
 * doesn't accidentally fire a reschedule.
 *
 * Honors `prefers-reduced-motion` (NFR-CUX-004) by virtue of using CSS
 * transitions only on `box-shadow` / colors, never on layout.
 */
export interface AppointmentBlockProps {
  appointment: Appointment;
  service?: Service;
  customer?: Customer;
  conflicts: SyncConflict[];
  /** Top in pixels relative to the column origin. */
  top: number;
  /** Height in pixels. */
  height: number;
  /** Disable resize (e.g. month view never has resize handles). */
  resizable: boolean;
  /** Optional left/width override for week-view multi-day layout. */
  style?: CSSProperties;
  onClick: () => void;
  /** Standard click-and-drag reschedule pickup (HTML5 native dragstart). */
  onDragStartReschedule?: (event: DragEvent<HTMLDivElement>, appointmentId: string) => void;
  /**
   * Resize commit handler. Receives the new `ends_at` (snapped + bounded so
   * the appointment stays at least one slot long). The parent commits via
   * `api.patchAppointment` and rolls back via `onResizeRollback` if the
   * server returns 409 slot_taken or any other error.
   */
  onResize?: (appointmentId: string, newEndsAt: string) => Promise<void> | void;
  /**
   * Precomputed resource name for inline display (REQ-PSR-008). Surfaces
   * that DO group by resource (day/week/resource calendar grids) leave this
   * unset — the column header already shows the resource, and the block
   * renders the appointment's `resource_name` snapshot ONLY if it diverges
   * from the live name (the rename case the PRD actually targets). Surfaces
   * that DON'T group by resource (dispatch, mixed lists) pass
   * `appointment.resource_name ?? resource.name` so the resource is always
   * visible.
   */
  resourceName?: string;
}

export function AppointmentBlock({
  appointment,
  service,
  customer,
  conflicts,
  top,
  height,
  resizable,
  style,
  onClick,
  onDragStartReschedule,
  onResize,
  resourceName
}: AppointmentBlockProps): React.JSX.Element {
  const colorKey = service?.id ?? "default";
  const slotIncrement = service?.slot_increment_minutes ?? 15;
  // Snapshot-first / caller-fallback / nothing. Callers that pass a fully
  // resolved `resourceName` get rendering for legacy rows too.
  const displayResourceName = appointment.resource_name ?? resourceName;

  // Local optimistic height during a resize gesture. `null` = not resizing.
  const [previewHeight, setPreviewHeight] = useState<number | null>(null);
  const [isResizing, setIsResizing] = useState(false);
  const resizing = useRef<{
    startY: number;
    startHeightPx: number;
  } | null>(null);
  // The window listeners read these values via refs so the listener-install
  // effect can depend only on the stable `isResizing` flag. If we listed the
  // ever-changing `previewHeight` in the deps array, every mousemove would
  // re-run the effect and tear down + reattach `mouseup`, opening a window
  // where the release event is dropped (NFR-CUX-003).
  const slotIncrementRef = useRef(slotIncrement);
  const appointmentRef = useRef(appointment);
  const onResizeRef = useRef(onResize);
  slotIncrementRef.current = slotIncrement;
  appointmentRef.current = appointment;
  onResizeRef.current = onResize;

  const onResizeMouseDown = (event: React.MouseEvent<HTMLDivElement>): void => {
    if (!resizable) return;
    if (!onResize) return;
    event.preventDefault();
    event.stopPropagation();
    resizing.current = { startY: event.clientY, startHeightPx: height };
    setPreviewHeight(height);
    setIsResizing(true);
  };

  useEffect(() => {
    if (!isResizing) return;
    const onMove = (event: MouseEvent): void => {
      const r = resizing.current;
      if (!r) return;
      const delta = event.clientY - r.startY;
      // Snap delta in pixels to whole rows, then quantize to the service's
      // slot_increment_minutes (REQ-CUX-007). The block must remain at least
      // one slot tall.
      const inc = slotIncrementRef.current;
      const minPx = (inc * ROW_HEIGHT_PX) / MINUTES_PER_ROW;
      const rawNewHeightPx = Math.max(minPx, r.startHeightPx + delta);
      const rawMinutesTotal = (rawNewHeightPx / ROW_HEIGHT_PX) * MINUTES_PER_ROW;
      const snappedMinutes = Math.max(inc, snapMinutes(rawMinutesTotal, inc));
      const snappedPx = (snappedMinutes * ROW_HEIGHT_PX) / MINUTES_PER_ROW;
      setPreviewHeight(snappedPx);
    };
    const onUp = async (event: MouseEvent): Promise<void> => {
      const r = resizing.current;
      resizing.current = null;
      if (!r) {
        setIsResizing(false);
        setPreviewHeight(null);
        return;
      }
      const inc = slotIncrementRef.current;
      const appt = appointmentRef.current;
      const handler = onResizeRef.current;
      const delta = event.clientY - r.startY;
      const minPx = (inc * ROW_HEIGHT_PX) / MINUTES_PER_ROW;
      const rawNewHeightPx = Math.max(minPx, r.startHeightPx + delta);
      const rawMinutesTotal = (rawNewHeightPx / ROW_HEIGHT_PX) * MINUTES_PER_ROW;
      const snappedMinutes = Math.max(inc, snapMinutes(rawMinutesTotal, inc));
      const startsMs = Date.parse(appt.starts_at);
      const newEndsAt = new Date(startsMs + snappedMinutes * 60_000).toISOString();
      const oldDuration = Math.round((Date.parse(appt.ends_at) - startsMs) / 60_000);
      if (snappedMinutes === oldDuration) {
        setIsResizing(false);
        setPreviewHeight(null);
        return;
      }
      try {
        await handler?.(appt.id, newEndsAt);
      } finally {
        // On success the parent reloads and the new height comes from props;
        // on failure (e.g. slot_taken) the prior `height` prop is unchanged
        // and dropping the preview snaps the block back (REQ-CUX-008).
        setIsResizing(false);
        setPreviewHeight(null);
      }
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
  }, [isResizing]);

  const renderedHeight = previewHeight ?? height;

  return (
    <div
      data-testid={`appointment-block-${appointment.id}`}
      data-appointment-id={appointment.id}
      draggable={Boolean(onDragStartReschedule)}
      onDragStart={
        onDragStartReschedule
          ? (event) => onDragStartReschedule(event, appointment.id)
          : undefined
      }
      onClick={onClick}
      style={{
        top,
        height: renderedHeight,
        background: colorForKey(colorKey),
        borderLeftColor: colorForKeyBorder(colorKey),
        ...style
      }}
      className={cn(
        "group absolute left-1 right-1 cursor-grab overflow-hidden rounded-md border border-border/60 border-l-[3px] px-2 py-1 text-[11px] shadow-xs transition-shadow duration-150 hover:shadow-md hover:z-[2] active:cursor-grabbing motion-reduce:transition-none",
        appointment.lifecycle_state === "cancelled" && "opacity-50 line-through",
        appointment.override_overlap && "ring-2 ring-warning/60"
      )}
      title={appointmentTooltip(appointment, service, customer, displayResourceName)}
    >
      <div className="truncate font-semibold leading-tight text-foreground">
        {service?.name ?? "Appointment"}
      </div>
      <div className="truncate leading-tight text-muted-foreground">
        {appointment.customer_name ?? customer?.name ?? "—"} · <span className="tabular-nums">{formatTime(appointment.starts_at)}</span>
        {displayResourceName && (
          <>
            {" · "}
            <span data-testid={`appointment-resource-${appointment.id}`}>{displayResourceName}</span>
          </>
        )}
      </div>
      <BannerStack appointment={appointment} conflicts={conflicts} />
      {resizable && onResize && (
        // Resize is a mouse-driven gesture (no touch DnD yet); hide the
        // handle on mobile so it doesn't promise a feature that needs a
        // pointer device. The appointment dialog's "Edit time" form lets
        // mobile users change duration via a number field instead.
        <div
          data-testid={`resize-handle-${appointment.id}`}
          aria-label="Resize appointment"
          role="slider"
          tabIndex={-1}
          onMouseDown={onResizeMouseDown}
          className="absolute inset-x-0 bottom-0 hidden h-1.5 cursor-ns-resize bg-transparent hover:bg-primary/30 lg:block"
        />
      )}
    </div>
  );
}

/**
 * PRD principal-scoped-reads-and-crm-refs (REQ-PSR-008). Prefer the
 * appointment's denormalized `resource_name` snapshot over the live
 * resources-table lookup — a CRM-side rename of the stylist must not mutate
 * already-booked appointments. The block doesn't receive the resource object
 * directly today, so the snapshot is appended to the tooltip; that keeps the
 * UI surface consistent without enlarging the prop contract.
 */
function appointmentTooltip(
  appointment: Appointment,
  service?: Service,
  customer?: Customer,
  resourceName?: string
): string {
  const serviceName = service?.name ?? "Appointment";
  const customerName = appointment.customer_name ?? customer?.name ?? "?";
  const base = `${serviceName} — ${customerName}`;
  return resourceName ? `${base} · ${resourceName}` : base;
}
